import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import * as api from '../services/api';

const AppContext = createContext(null);

const CTX_KEY = 'payroll.activeContext';

function loadStoredContext() {
  try {
    const raw = localStorage.getItem(CTX_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

const initialState = {
  tables: [],
  currentTable: null,
  records: [],
  loading: false,
  error: null,
  activeContext: loadStoredContext(),
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':        return { ...state, loading: action.payload };
    case 'SET_TABLES':         return { ...state, tables: action.payload };
    case 'SET_CURRENT_TABLE':  return { ...state, currentTable: action.payload, records: [] };
    case 'SET_RECORDS':        return { ...state, records: action.payload };
    case 'SET_ERROR':          return { ...state, error: action.payload };
    case 'SET_ACTIVE_CONTEXT': return { ...state, activeContext: action.payload };
    default: return state;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Reload the master tables list (drives sidebar row counts).
  const loadTables = useCallback(async () => {
    const res = await api.fetchTables();
    dispatch({ type: 'SET_TABLES', payload: res.data.data });
  }, []);

  // Reload the current table's records straight from the API.
  // Caller passes the table name so we don't depend on a stale state closure.
  const reloadRecords = useCallback(async (tableName) => {
    if (!tableName) return;
    const res = await api.fetchRecords(tableName);
    dispatch({ type: 'SET_RECORDS', payload: res.data.data });
  }, []);

  // Switch to a table: set as current and load its rows from DB.
  const selectTable = useCallback(async (tableName) => {
    dispatch({ type: 'SET_CURRENT_TABLE', payload: tableName });
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const res = await api.fetchRecords(tableName);
      dispatch({ type: 'SET_RECORDS', payload: res.data.data });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // After every mutation we re-fetch from the database (records + table counts)
  // so the UI always reflects what's actually persisted, never an optimistic guess.
  const addRecord = useCallback(async (tableName, data) => {
    const res = await api.createRecord(tableName, data);
    await Promise.all([reloadRecords(tableName), loadTables()]);
    return res.data;
  }, [reloadRecords, loadTables]);

  const editRecord = useCallback(async (tableName, id, data) => {
    const res = await api.updateRecord(tableName, id, data);
    await Promise.all([reloadRecords(tableName), loadTables()]);
    return res.data;
  }, [reloadRecords, loadTables]);

  const removeRecord = useCallback(async (tableName, id) => {
    await api.deleteRecord(tableName, id);
    await Promise.all([reloadRecords(tableName), loadTables()]);
  }, [reloadRecords, loadTables]);

  // ── Active payroll context (company-scoped working session) ──────────────
  const setActiveContext = useCallback((ctx) => {
    if (ctx) {
      localStorage.setItem(CTX_KEY, JSON.stringify(ctx));
    } else {
      localStorage.removeItem(CTX_KEY);
    }
    dispatch({ type: 'SET_ACTIVE_CONTEXT', payload: ctx });
  }, []);

  const clearActiveContext = useCallback(() => setActiveContext(null), [setActiveContext]);

  useEffect(() => {
    loadTables().catch(() => {});
    if (state.currentTable) {
      reloadRecords(state.currentTable).catch(() => {});
    }
  }, [
    state.activeContext?.company_id,
    state.activeContext?.bg_id,
    state.activeContext?.ldg_id,
    state.activeContext?.payroll_id,
    state.currentTable,
    loadTables,
    reloadRecords,
  ]);

  // Keep storage in sync across tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === CTX_KEY) {
        dispatch({ type: 'SET_ACTIVE_CONTEXT', payload: e.newValue ? JSON.parse(e.newValue) : null });
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <AppContext.Provider
      value={{ state, loadTables, reloadRecords, selectTable, addRecord, editRecord, removeRecord,
               setActiveContext, clearActiveContext }}
    >
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
